import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';

function Block({ style, color }) {
  return <View style={[{ backgroundColor: color, borderRadius: 12 }, style]} />;
}

/** Reserves VerticalTemplate hero + section height while use-case data loads. */
export default function UseCasePageSkeleton() {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const block = colors.solid_color_tertiary;
  const pad = isXS ? 20 : 40;

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    scroll: { flex: 1, width: '100%' },
    hero: {
      width: '100%',
      alignItems: 'center',
      paddingHorizontal: pad,
      paddingTop: isXS ? 100 : 140,
      paddingBottom: isXS ? 60 : 80,
      minHeight: isXS ? 420 : 520,
    },
    icon: { width: 60, height: 60, borderRadius: 18, marginBottom: 24 },
    title: { width: isXS ? '88%' : 520, height: isXS ? 36 : 44, borderRadius: 10, marginBottom: 16 },
    subtitle: { width: isXS ? '92%' : 640, height: 16, borderRadius: 8, marginBottom: 10 },
    subtitleShort: { width: isXS ? '70%' : 480, height: 16, borderRadius: 8, marginBottom: 32 },
    ctaRow: { flexDirection: isXS ? 'column' : 'row', gap: 12, width: '100%', maxWidth: 560 },
    cta: { height: isXS ? 52 : 56, borderRadius: 999, flex: isXS ? undefined : 1 },
    section: {
      width: '100%',
      paddingHorizontal: pad,
      paddingVertical: isXS ? 64 : 80,
      gap: 16,
      alignItems: 'center',
    },
    sectionTitle: { width: 280, height: 28, borderRadius: 8 },
    gridRow: { flexDirection: isXS ? 'column' : 'row', gap: 16, width: '100%', maxWidth: 1000 },
    gridCard: { flex: isXS ? undefined : 1, height: isXS ? 120 : 140, borderRadius: 20 },
  }), [block, colors.solid_color_primary, isXS, pad]);

  return (
    <View style={styles.container} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      <ScrollView style={styles.scroll} contentContainerStyle={{ alignItems: 'center' }}>
        <View style={styles.hero}>
          <Block style={styles.icon} color={block} />
          <Block style={styles.title} color={block} />
          <Block style={styles.subtitle} color={block} />
          <Block style={styles.subtitleShort} color={block} />
          <View style={styles.ctaRow}>
            <Block style={styles.cta} color={block} />
            <Block style={styles.cta} color={block} />
          </View>
        </View>
        <View style={styles.section}>
          <Block style={styles.sectionTitle} color={block} />
          <View style={styles.gridRow}>
            <Block style={styles.gridCard} color={block} />
            <Block style={styles.gridCard} color={block} />
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
